import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckCircle2, RotateCcw, FileText } from 'lucide-react';
import client from '../../api/client';
import { NAVY, AMBER, AMBER_DARK, BORDER_COLOR, bubbleStyle } from '../../styles/tokens';

const STATUS_CONFIG = {
  draft:     { label: 'Borrador',  color: '#7c3aed', bg: '#f3e8ff', icon: FileText },
  submitted: { label: 'Enviado',   color: '#ca8a04', bg: '#fef9c3', icon: Clock },
  approved:  { label: 'Aprobado',  color: '#16a34a', bg: '#dcfce7', icon: CheckCircle2 },
  returned:  { label: 'Devuelto',  color: '#ea580c', bg: '#fed7aa', icon: RotateCcw },
};

function fmtWeek(dateStr) {
  const d = new Date(dateStr);
  const end = new Date(d);
  end.setDate(end.getDate() + 4);
  return `${d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })} — ${end.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}`;
}

export default function TimesheetHistoryPage() {
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get('/api/timesheets', { params: { view: 'my' } })
      .then(r => setTimesheets(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? timesheets : timesheets.filter(t => t.status === filter);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>;

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 800, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: 0 }}>Historial de Timesheets</h1>
        <button onClick={() => navigate('/horas')} style={{
          background: AMBER, color: '#fff', border: 'none', borderRadius: 6,
          padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          fontFamily: 'Inter, sans-serif',
        }}>
          Semana Actual
        </button>
      </div>

      {/* Filter pills */}
      <div className="filter-bar" style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: 'Todos' },
          { key: 'draft', label: 'Borradores' },
          { key: 'submitted', label: 'Enviados' },
          { key: 'approved', label: 'Aprobados' },
          { key: 'returned', label: 'Devueltos' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: '5px 14px', fontSize: 12, fontWeight: 600,
              borderRadius: 20, cursor: 'pointer',
              border: `1.5px solid ${filter === f.key ? AMBER : BORDER_COLOR}`,
              background: filter === f.key ? '#fef3c7' : '#fff',
              color: filter === f.key ? AMBER_DARK : '#64748b',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div style={{ ...bubbleStyle, padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
          No hay timesheets para mostrar.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(ts => {
            const cfg = STATUS_CONFIG[ts.status] || STATUS_CONFIG.draft;
            return (
              <div
                key={ts.id}
                onClick={() => navigate(`/horas/${ts.id}`)}
                style={{
                  ...bubbleStyle, padding: '14px 18px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 14,
                  borderLeft: `3px solid ${cfg.color}`,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: NAVY, marginBottom: 2 }}>
                    {fmtWeek(ts.week_start)}
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    {parseFloat(ts.total_hours)} hora{parseFloat(ts.total_hours) !== 1 ? 's' : ''}
                  </div>
                </div>
                <div style={{
                  padding: '3px 10px', borderRadius: 10,
                  background: cfg.bg, color: cfg.color,
                  fontSize: 11, fontWeight: 600,
                }}>
                  {cfg.label}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
